import React, { useState, useEffect } from 'react';
import { getCurrentRace, setCurrentRace, getDrivers, getTeams, getUsers, getRaces, getRace, setRaceLock, setRaceLockTime, syncRaces, syncTeams, syncDrivers } from '../../api';
import RaceSelector from '../../components/RaceSelector';
import AdminDriverLineup from './AdminDriverLineup';
import AdminAddDriver from './AdminAddDriver';
import AdminAddRace from './AdminAddRace';
import AdminResults from './AdminResults';
import AdminBonusQuestions from './AdminBonusQuestions';
import AdminUsers from './AdminUsers';
import AdminApiData from './AdminApiData';
import { useAppAlert } from '../../components/AppAlerts';

export default function Admin() {
    const [currentRace, setCurrentRaceState] = useState('');
    const [drivers, setDrivers] = useState([]);
    const [teams, setTeams] = useState([]);
    const [users, setUsers] = useState([]);
    const [races, setRaces] = useState([]);
    const [racesMap, setRacesMap] = useState({});
    const [message, setMessage] = useState('');
    const [lockState, setLockState] = useState(false);
    const [lockOffsetMin, setLockOffsetMin] = useState(5); // minutes after race start (4 min + 1 min grace)
    const [raceStartTime, setRaceStartTime] = useState(null); // Date object
    const notify = useAppAlert();

    const getAlertType = (text) => {
        if (!text) return 'success';
        const lower = text.toLowerCase();
        return /error|unable|failed|invalid|select|provide|please|cannot|no user|not set|network/.test(lower) ? 'error' : 'success';
    };

    const showAlert = (text, typeOverride) => {
        if (!text) return;
        const type = typeOverride || getAlertType(text);
        setMessage(text);
        notify(text, type);
    };

    async function loadRaces() {
        const list = await getRaces();
        setRaces(list || []);
        const map = {};
        (list || []).forEach((r) => { map[String(r.id)] = r.name; });
        setRacesMap(map);
    }

    async function loadDrivers() {
        const list = await getDrivers();
        setDrivers(list || []);
    }

    async function loadTeams() {
        const list = await getTeams();
        setTeams(list || []);
    }

    useEffect(() => {
        loadDrivers();
        loadTeams();
        getUsers().then(setUsers);
        loadRaces();
        getCurrentRace().then((id) => setCurrentRaceState(id));
    }, []);

    // Load pick_lock_time for the selected race so the input stays in sync
    useEffect(() => {
        if (!currentRace) { setRaceStartTime(null); setLockOffsetMin(5); return; }
        getRace(currentRace)
            .then((r) => {
                if (!r || !r.start_time) { setRaceStartTime(null); setLockOffsetMin(5); return; }
                const start = new Date(r.start_time);
                if (isNaN(start)) { setRaceStartTime(null); setLockOffsetMin(5); return; }
                setRaceStartTime(start);
                if (r.pick_lock_time) {
                    const lock = new Date(r.pick_lock_time);
                    if (!isNaN(lock)) {
                        setLockOffsetMin(Math.round((lock - start) / 60000));
                        return;
                    }
                }
                setLockOffsetMin(5);
            })
            .catch(() => { setRaceStartTime(null); setLockOffsetMin(5); });
    }, [currentRace]);

    async function handleSetCurrent(id) {
        await setCurrentRace(id);
        setCurrentRaceState(id);
        showAlert('Current race set to ' + (racesMap[String(id)] || id));
    }

    async function handleToggleLock() {
        if (!currentRace) return showAlert('Select a race to lock/unlock');
        try {
            const res = await setRaceLock(currentRace, !lockState);
            const locked = res && typeof res.locked !== 'undefined' ? !!res.locked : !!(!lockState);
            setLockState(locked);
            showAlert(`${racesMap[String(currentRace)] || currentRace} ${locked ? 'locked' : 'unlocked'}`);
        } catch (err) {
            console.error('handleToggleLock error', err);
            showAlert('Unable to update lock state');
        }
    }

    async function handleAdjustLockTime(deltaMins) {
        if (!currentRace || !raceStartTime) return showAlert('Select a race first');
        const newOffset = lockOffsetMin + deltaMins;
        try {
            const lockDate = new Date(raceStartTime.getTime() + newOffset * 60000);
            await setRaceLockTime(currentRace, lockDate.toISOString());
            setLockOffsetMin(newOffset);
            showAlert(`Pick lock time set to race start + ${newOffset} min`);
        } catch (err) {
            console.error('handleAdjustLockTime error', err);
            showAlert('Unable to update lock time');
        }
    }

    async function handleClearLockTime() {
        if (!currentRace) return;
        try {
            await setRaceLockTime(currentRace, null);
            setLockOffsetMin(5);
            showAlert('Pick lock time cleared — defaulting to race start + 5 min (4 min + 1 min grace)');
        } catch (err) {
            console.error('handleClearLockTime error', err);
            showAlert('Unable to clear lock time');
        }
    }

    async function handleSyncRaces() {
        try {
            const result = await syncRaces('current');
            await loadRaces();
            showAlert(`Race sync complete: ${result.synced || 0} races loaded`);
        } catch (err) {
            console.error('handleSyncRaces error', err);
            showAlert('Unable to sync races');
        }
    }

    async function handleSyncTeams() {
        try {
            const result = await syncTeams('current');
            await loadTeams();
            showAlert(`Team sync complete: ${result.synced || 0} teams loaded`);
        } catch (err) {
            console.error('handleSyncTeams error', err);
            showAlert('Unable to sync teams');
        }
    }

    async function handleSyncDrivers() {
        try {
            const result = await syncDrivers('current');
            await loadDrivers();
            showAlert(`Driver sync complete: ${result.synced || 0} drivers loaded`);
        } catch (err) {
            console.error('handleSyncDrivers error', err);
            showAlert('Unable to sync drivers');
        }
    }

    return (
        <div className="admin-page">
            <h3>Admin: Manage Races &amp; Publish Results</h3>

            <h5 className="mb-3">Race Controls</h5>
            <div className="card mb-3">
                <div className="card-body">
                    <label className="form-label">Current Active Race</label>
                    <div className="d-flex gap-2 align-items-center">
                        <RaceSelector value={currentRace} onChange={handleSetCurrent} includePlaceholder={false} />
                        <button className="btn btn-outline-danger" type="button" onClick={handleToggleLock}>
                            {lockState ? 'Unlock' : 'Lock'}
                        </button>
                    </div>
                    <div className="mt-3">
                        <label className="form-label">Pick Lock Time</label>
                        <div className="d-flex gap-2 align-items-center flex-wrap">
                            <span className="fw-semibold">Race start + {lockOffsetMin} min</span>
                            <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => handleAdjustLockTime(-1)}>−1 min</button>
                            <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => handleAdjustLockTime(1)}>+1 min</button>
                            {lockOffsetMin !== 4 && (
                                <button className="btn btn-outline-secondary btn-sm" type="button" onClick={handleClearLockTime}>Reset</button>
                            )}
                        </div>
                        <div className="small text-muted mt-1">Default is race start + 4 min</div>
                    </div>
                </div>
            </div>

            <AdminBonusQuestions currentRace={currentRace} setMessage={showAlert} />
            <AdminResults
                currentRace={currentRace}
                drivers={drivers}
                racesMap={racesMap}
                setMessage={showAlert}
                lockState={lockState}
                setLockState={setLockState}
            />

            <AdminAddDriver
                teams={teams}
                setMessage={showAlert}
                onCreated={async (drv) => { await loadDrivers(); showAlert(`Added driver ${drv.first_name || drv.firstName} ${drv.last_name || drv.lastName}`); }}
            />

            <AdminAddRace
                setMessage={showAlert}
                onCreated={async (r) => { await loadRaces(); showAlert(`Added race ${r.name || r.race_name}`); }}
            />
            <AdminDriverLineup currentRace={currentRace} setMessage={showAlert} />

            <AdminUsers users={users} />
            <AdminApiData
                races={races}
                teams={teams}
                drivers={drivers}
                currentRace={currentRace}
                onSyncRaces={handleSyncRaces}
                onSyncTeams={handleSyncTeams}
                onSyncDrivers={handleSyncDrivers}
            />
        </div>
    );
}
